// src/components/chatbot/FloatingChatWidget.jsx
import React, { useState, useRef, useEffect } from "react";
import "./ChatBot.css";
import chatIcon from "../../assets/images/chat-icon.png";

const FloatingChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    const saved = localStorage.getItem("chatMessages");
    return saved
      ? JSON.parse(saved)
      : [{ type: "bot", text: "👋 Hi! I'm your Finx Assistant. How can I help you today?" }];
  });
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  const chatRef = useRef(null);
  const endOfMessagesRef = useRef(null);

  const currentReader = useRef(null);
  const currentAbortController = useRef(null);
  const isUnmounted = useRef(false);

  const emojiMap = {
    pricing: "💰",
    cost: "💰",
    planning: "🗓️",
    investment: "📈",
    job: "💼",
    duration: "⏳",
    hour: "⏱️",
    minute: "⏱️",
    audience: "🎯",
    objective: "🎯",
    training: "📅",
    location: "📍",
    default: "🔹",
    search: "🔍",
    link: "🔗"
  };

  const formatBotResponse = (text) => {
    if (!text) return [""];
  
    const lines = text.split("\n").filter(Boolean);
    const formatted = [];
    let subpointMode = false;
    let numberedMode = false;
    let isFirstLineOfBlock = true;
  
    lines.forEach((line, idx) => {
      let trimmed = line.trim();
      trimmed = trimmed.replace(/,+$/, ""); // Remove trailing commas
      trimmed = trimmed.replace(/^[-•–]\s*/, ""); // Remove bullet chars
      const lower = trimmed.toLowerCase();
  
      // First greeting (no default bullet)
      if (trimmed.startsWith("👋 hi")) {
        formatted.push(trimmed);
        return;
      }
  
      if (isFirstLineOfBlock) {
        if (lower.includes("search")) {
          formatted.push(`🔍 ${trimmed}`);
        } else {
          formatted.push(`${trimmed}`);
        }
        isFirstLineOfBlock = false;
        return;
      }

      // Bold Course Name or Job Title with line break before
      if (lower.startsWith("course name:") || lower.startsWith("title:")) {
        const parts = trimmed.split(":");
        const name = parts.slice(1).join(":").trim();
        // formatted.push("<br/>" + `${emojiMap.default} <b>${parts[0]}: ${name}</b>`);
        formatted.push(`<br/>${emojiMap.default} <b>${parts[0]}: ${name}</b>`);
        return;
      }
  
      // Replace any raw course links with "Link"
      if (lower.includes("link:")) {
        const [label, ...rest] = trimmed.split(":");
        const url = rest.join(":").trim();
        if (/https?:\/\//i.test(url)) {
          formatted.push(`${emojiMap.link} ${label.trim()}: <a href="${url}" target="_blank" rel="noopener noreferrer">Link</a>`);
          return;
        }
      }

      // Handle any raw standalone URLs
      if (/https?:\/\/\S+/i.test(trimmed)) {
        const urlMatch = trimmed.match(/https?:\/\/\S+/i);
        const url = urlMatch ? urlMatch[0] : "";
        const beforeText = trimmed.replace(url, "").trim();
        formatted.push(`${emojiMap.default} ${beforeText} ${emojiMap.link} <a href="${url}" target="_blank" rel="noopener noreferrer">Link</a>`);
        return;
      }
  
      // Keep Search emoji if explicitly mentioned
      if (lower.startsWith("search") || lower.startsWith("🔍")) {
        formatted.push(`🔍 ${trimmed}`);
        return;
      }
  
      // Duration & Audience
      if (lower.startsWith("course duration:") || lower.startsWith("duration:")) {
        formatted.push(`⏳ ${trimmed}`);
        return;
      }
      if (lower.startsWith("target audience") || lower.startsWith("audience:")) {
        formatted.push(`🎯 ${trimmed}`);
        return;
      }
      if (lower.startsWith("location:")) {
        formatted.push(`📍 ${trimmed}`);
        return;
      }
  
      // Headers ending with colon
      if (trimmed.endsWith(":")) {
        const headerText = trimmed.slice(0, -1).trim();
        const emoji =
          Object.entries(emojiMap).find(([key]) =>
            headerText.toLowerCase().includes(key)
          )?.[1] || emojiMap.default;
        formatted.push(`${emoji} ${headerText}`);
        subpointMode = true;
        numberedMode = headerText.toLowerCase().includes("course content");
        return;
      }
  
      // Subpoints / numbered mode
      if (subpointMode) {
        if (numberedMode && /^\d+\./.test(trimmed)) {
          formatted.push(`&nbsp;&nbsp;&nbsp;${trimmed}`);
        } else {
          formatted.push(`&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;${emojiMap.default} ${trimmed}`);
        }
  
        if (!lines[idx + 1] || lines[idx + 1].trim().endsWith(":") || lines[idx + 1].trim() === "") {
          subpointMode = false;
          numberedMode = false;
        }
        return;
      }
  
      if (/^[\p{Emoji}]/u.test(trimmed)) {
        formatted.push(trimmed);
      } else if (trimmed) {
        formatted.push(`${emojiMap.default} ${trimmed}`);
      }
    });
  
    return formatted;
  };

  function generateSessionId() {
    return "session-" + Math.random().toString(36).substring(2, 15);
  }

  const [sessionId, setSessionId] = useState(() => {
    return localStorage.getItem("agentSessionId") || generateSessionId();
  });

  useEffect(() => {
    localStorage.setItem("agentSessionId", sessionId);
  }, [sessionId]);

  const cancelActiveStream = async () => {
    if (currentAbortController.current) {
      try {
        currentAbortController.current.abort();
      } catch (_) {}
      currentAbortController.current = null;
    }
    if (currentReader.current) {
      try {
        await currentReader.current.cancel();
      } catch (_) {}
      try {
        if (typeof currentReader.current.releaseLock === "function") {
          currentReader.current.releaseLock();
        }
      } catch (_) {}
      currentReader.current = null;
    }
    setMessages((prev) => prev.filter((m) => m.type !== "bot-temp"));
  };

  const sendMessage = async (message, retry = false) => {
    if (!message.trim() || isTyping) return;
  
    if (message.trim().toLowerCase() === "clear") {
      await cancelActiveStream();
      const initialMsg = {
        type: "bot",
        text: "👋 Hi! I'm your Finx Assistant. How can I help you today?",
      };
      setMessages([initialMsg]);
      localStorage.setItem("chatMessages", JSON.stringify([initialMsg]));
      setInput("");
      return;
    }
  
    await cancelActiveStream();
  
    // Add user message only once (skip on retry)
    if (!retry) {
      setMessages((prev) => [...prev, { type: "user", text: message }]);
    }
  
    setInput("");
    setIsTyping(true);
  
    const controller = new AbortController();
    currentAbortController.current = controller;
  
    try {
      // Always get current sessionId from localStorage
      let currentSessionId = localStorage.getItem("agentSessionId") || sessionId;
  
      const url = `https://ges5js6big.execute-api.ap-south-1.amazonaws.com/production/finxstream?message=${encodeURIComponent(
        message
      )}&sessionId=${currentSessionId}`;
  
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "text/plain, */*",
          "Cache-Control": "no-cache",
        },
        signal: controller.signal,
      });
  
      // Always sync sessionId from Lambda if returned
      const newSessionId = response.headers.get("x-session-id");
      if (newSessionId && newSessionId !== currentSessionId) {
        localStorage.setItem("agentSessionId", newSessionId);
        setSessionId(newSessionId);
        currentSessionId = newSessionId;
      }
  
      if (response.status === 500) {
        console.warn("Got 500 from API...");
        // Only create new session and retry on first failure
        if (!retry) {
          const freshSessionId = generateSessionId();
          console.log("🔄 Generating new sessionId due to 500:", freshSessionId);
          localStorage.setItem("agentSessionId", freshSessionId);
          setSessionId(freshSessionId);
          return sendMessage(message, true); // retry once with new session
        } else {
          throw new Error("Repeated 500 error even after new session");
        }
      }
  
      if (!response.ok) {
        let errText = "";
        try {
          errText = await response.text();
        } catch (_) {}
        throw new Error(`HTTP ${response.status}: ${errText || "Request failed"}`);
      }
  
      const botMessage = await response.text();
      if (botMessage && !isUnmounted.current) {
        setMessages((prev) => [...prev, { type: "bot", text: botMessage }]);
      }
    } catch (error) {
      console.error("Chat error:", error);
      if (!retry) {
        console.warn("Retrying due to error...");
        return sendMessage(message, true);
      }
      if (!isUnmounted.current) {
        setMessages((prev) => [
          ...prev,
          { type: "bot", text: "⚠️ Something went wrong. Please try again." },
        ]);
      }
    } finally {
      if (!isUnmounted.current) setIsTyping(false);
    }
  };  

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  useEffect(() => {
    if (endOfMessagesRef.current) {
      endOfMessagesRef.current.scrollIntoView({ behavior: "smooth" });
    }
    localStorage.setItem("chatMessages", JSON.stringify(messages));
  }, [messages, isTyping]);

  useEffect(() => {
    return () => {
      isUnmounted.current = true;

      if (currentAbortController.current) {
        try {
          currentAbortController.current.abort();
        } catch (_) {}
        currentAbortController.current = null;
      }

      if (currentReader.current) {
        try {
          currentReader.current.cancel();
        } catch (_) {}
        try {
          if (typeof currentReader.current.releaseLock === "function") {
            currentReader.current.releaseLock();
          }
        } catch (_) {}
        currentReader.current = null;
      }
    };
  }, []);

  return (
    <div className="chat-widget-wrapper">
      {!isOpen && (
        <button className="chat-toggle-button" onClick={() => setIsOpen(true)}>
          <img src={chatIcon} alt="Chat" />
        </button>
      )}

      {isOpen && (
        <div className="floating-chat-box">
          <div className="chat-header">
            <span role="img" aria-label="AI Buddy">🤖 Finx AI Buddy – Your 24×7 Assistant</span>
            <button className="close-btn" onClick={() => setIsOpen(false)}>
              ×
            </button>
          </div>

          <div className="chat-body" ref={chatRef}>
            {messages.map((msg, idx) => {
              const content =
                msg.type === "bot"
                  ? formatBotResponse(msg.text).join("<br/>")
                  : msg.text;
              return (
                <div
                  key={idx}
                  className={`message ${msg.type}`}
                  dangerouslySetInnerHTML={{ __html: content }}
                ></div>
              );
            })}
            {isTyping && <div className="message bot">Wait...</div>}
            <div ref={endOfMessagesRef} />
          </div>

          <div className="chat-input-area">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              rows={1}
            />
            <button onClick={() => sendMessage(input)} disabled={isTyping || !input.trim()}>
              ➤
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FloatingChatWidget;
